'use strict';

/**
 * Plansheet's "visually map out your workflow" section.
 *
 * A thin host around the four retold-workflow views (catalog, designer/map, board, subject
 * detail). It owns the tab bar and the panes; the views themselves are product-agnostic and come
 * from retold-workflow. Navigation between them is wired here: pick a workflow in the catalog to
 * open the designer or the board; click a card on the board to see its timeline, metrics, and
 * agency. The views talk to the server through the WorkflowAPI provider.
 *
 * @author Steven Velozo <steven@velozo.com>
 * @license MIT
 */

const libPictView = require('pict-view');

const _TABS =
[
	{ Key: 'catalog', Label: 'Catalog' },
	{ Key: 'designer', Label: 'Designer' },
	{ Key: 'board', Label: 'Board' },
	{ Key: 'subject', Label: 'Subject' }
];

const _ViewConfiguration =
{
	ViewIdentifier: 'Plansheet-Workflow',
	DefaultRenderable: 'Plansheet-Workflow',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,

	CSS: /*css*/`
		.psw { display: flex; flex-direction: column; height: 100%; padding: 12px 16px; box-sizing: border-box; }
		.psw-tabs { flex-shrink: 0; display: flex; gap: 4px; border-bottom: 1px solid var(--theme-color-border-light, #e3e3e3); margin-bottom: 10px; }
		.psw-tab { padding: 7px 14px; border: none; background: transparent; cursor: pointer; font-size: 14px; color: var(--theme-color-text-secondary, #667); border-bottom: 2px solid transparent; }
		.psw-tab:hover { color: var(--theme-color-text-primary, #222); }
		.psw-tab-active { color: var(--theme-color-brand-primary, #2e7d74); border-bottom-color: var(--theme-color-brand-primary, #2e7d74); font-weight: 600; }
		.psw-content { flex: 1; min-height: 0; display: flex; flex-direction: column; }
		.psw-pane { flex: 1; min-height: 0; display: flex; flex-direction: column; }
		.psw-catalog-host { flex: 1; min-height: 0; }
		.psw-import-note { font-size: 0.85em; color: var(--theme-color-text-secondary, #5b6376); padding: 4px 0 12px; }
		.psw-import-note a { color: var(--theme-color-brand-primary, #3b6fd4); text-decoration: none; }
		.psw-import-note a:hover { text-decoration: underline; }
	`,

	Templates:
	[
		{
			Hash: 'Plansheet-Workflow',
			Template: /*html*/`
<div class="psw">
	<h2 class="ps-page-h">Workflow</h2>
	<div class="psw-tabs">{~TS:Plansheet-Workflow-Tab:AppData.WorkflowShell.Tabs~}</div>
	<div class="psw-content">
		{~TS:Plansheet-Workflow-Pane-Catalog:AppData.WorkflowShell.CatalogSlot~}
		{~TS:Plansheet-Workflow-Pane-Map:AppData.WorkflowShell.MapSlot~}
		{~TS:Plansheet-Workflow-Pane-Board:AppData.WorkflowShell.BoardSlot~}
		{~TS:Plansheet-Workflow-Pane-Subject:AppData.WorkflowShell.SubjectSlot~}
	</div>
</div>`
		},
		{ Hash: 'Plansheet-Workflow-Tab', Template: /*html*/`<button class="psw-tab {~D:Record.ActiveClass~}" onclick="_Pict.views['Plansheet-Workflow'].showTab('{~D:Record.Key~}')">{~D:Record.Label~}</button>` },
		{ Hash: 'Plansheet-Workflow-Pane-Catalog', Template: /*html*/`<div class="psw-pane"><div class="psw-import-note">These are the crafts you have imported. Bring a new one in from <a href="#/settings/draftingkit">Settings &rsaquo; Drafting Kit</a>.</div><div class="psw-catalog-host" id="Workflow-Catalog-Container"></div></div>` },
		{ Hash: 'Plansheet-Workflow-Pane-Map', Template: /*html*/`<div class="psw-pane" id="Workflow-Map-Container"></div>` },
		{ Hash: 'Plansheet-Workflow-Pane-Board', Template: /*html*/`<div class="psw-pane" id="Workflow-Board-Container"></div>` },
		{ Hash: 'Plansheet-Workflow-Pane-Subject', Template: /*html*/`<div class="psw-pane" id="Workflow-Subject-Container"></div>` }
	],

	Renderables:
	[
		{ RenderableHash: 'Plansheet-Workflow', TemplateHash: 'Plansheet-Workflow', ContentDestinationAddress: '#Plansheet-Workspace', RenderMethod: 'replace' }
	]
};

class PictViewWorkflow extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		if (!this.pict.AppData.WorkflowShell)
		{
			this.pict.AppData.WorkflowShell = { ActiveTab: 'catalog', SelectedType: null, Tabs: [], CatalogSlot: [], MapSlot: [], BoardSlot: [], SubjectSlot: [] };
		}
	}

	_state() { return this.pict.AppData.WorkflowShell; }

	/** Enter the section: land on the catalog. The RENDER half, so the route does not re-enter itself. */
	show()
	{
		this._applyShowTab('catalog');
	}

	/** Enter the section on a named tab, from the /workflow/:tab route. */
	showTabRoute(pTab)
	{
		this._applyShowTab(_TABS.some((pTabDef) => pTabDef.Key === pTab) ? pTab : 'catalog');
	}

	/**
	 * Switch tabs. NAVIGATES; the route handler renders.
	 *
	 * The four tabs are four different pages -- the catalog, the designer, the running board, and a
	 * subject -- and only one of them could be linked or reloaded into. openSubject was routed earlier
	 * for exactly that reason; this is the rest of the same job.
	 */
	showTab(pTab)
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showWorkflowTab === 'function' && tmpApp.showWorkflowTab(pTab)) { return; }
		return this._applyShowTab(pTab);
	}

	_applyShowTab(pTab)
	{
		let tmpState = this._state();
		tmpState.ActiveTab = pTab;
		tmpState.Tabs = _TABS.map((pTabDef) => ({ Key: pTabDef.Key, Label: pTabDef.Label, ActiveClass: (pTabDef.Key === pTab) ? 'psw-tab-active' : '' }));
		tmpState.CatalogSlot = (pTab === 'catalog') ? [{}] : [];
		tmpState.MapSlot = (pTab === 'designer') ? [{}] : [];
		tmpState.BoardSlot = (pTab === 'board') ? [{}] : [];
		tmpState.SubjectSlot = (pTab === 'subject') ? [{}] : [];
		this.render();
		this._renderActive(pTab);
	}

	_renderActive(pTab)
	{
		let tmpState = this._state();
		if (pTab === 'catalog') { this._view('Workflow-Catalog').reload(); }
		else if (pTab === 'designer') { if (tmpState.SelectedType) { this._view('Workflow-Map').showType(tmpState.SelectedType); } else { this._view('Workflow-Map').render(); } }
		else if (pTab === 'board') { if (tmpState.SelectedType) { this._view('Workflow-Board').showType(tmpState.SelectedType); } else { this._view('Workflow-Board').render(); } }
		else if (pTab === 'subject') { this._view('Workflow-Subject').render(); }
	}

	_view(pHash) { return this.pict.views[pHash]; }

	// -- navigation from the sub-views (wired as their callbacks) ---------------

	openDesigner(pTypeRecord) { this._state().SelectedType = pTypeRecord; this.showTab('designer'); }
	openBoard(pTypeRecord) { this._state().SelectedType = pTypeRecord; this.showTab('board'); }

	// Opening a subject is a NAVIGATION to /workflow/subject/:id, so it deep-links and survives a reload
	// (it used to reset to the catalog tab). The type/title we have are stashed for the route-driven open;
	// a cold deep-link has neither, and showSubject fetches everything by id, so it still resolves.
	openSubject(pSubjectID, pTypeRecord, pTitle)
	{
		this._pendingSubject = { Type: pTypeRecord || this._state().SelectedType || null, Title: pTitle || '' };
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showWorkflowSubject === 'function') { tmpApp.showWorkflowSubject(pSubjectID); }
		else { this._showSubjectRender(pSubjectID, this._pendingSubject.Type, this._pendingSubject.Title); }
	}

	// Route entry for /workflow/subject/:id: land on the subject tab and load the subject (using the stashed
	// type/title when we have them from an in-app open).
	showSubjectRoute(pSubjectID)
	{
		let tmpPending = this._pendingSubject || {};
		this._pendingSubject = null;
		this._showSubjectRender(Number(pSubjectID) || 0, tmpPending.Type || this._state().SelectedType || null, tmpPending.Title || '');
	}

	_showSubjectRender(pSubjectID, pTypeRecord, pTitle)
	{
		this.showTab('subject');
		this._view('Workflow-Subject').showSubject(pSubjectID, pTypeRecord, pTitle);
	}

	onAfterRender(pRenderable, pRenderDestinationAddress, pRecord, pContent)
	{
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable, pRenderDestinationAddress, pRecord, pContent);
	}
}

module.exports = PictViewWorkflow;
module.exports.default_configuration = _ViewConfiguration;
